import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Search,
  ShoppingCart,
  Fuel,
  Building2,
  Car,
  Package,
  Download,
} from "lucide-react";

const CATEGORIAS = [
  { value: "supermercado", label: "Supermercado", icon: ShoppingCart, color: "bg-green-500" },
  { value: "gasolinera", label: "Gasolinera", icon: Fuel, color: "bg-orange-500" },
  { value: "oficina", label: "Artículos de Oficina", icon: Building2, color: "bg-blue-500" },
  { value: "automotriz", label: "Automotriz", icon: Car, color: "bg-purple-500" },
  { value: "otro", label: "Otro", icon: Package, color: "bg-slate-500" },
];

const SettingsProveedores = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("all");
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const [formData, setFormData] = useState({
    razon_social: "",
    nombre_comercial: "",
    rfc: "",
    categoria: "otro",
    direccion: "",
    telefono: "",
    email: "",
    sitio_web: "",
    contacto: "",
    notas: "",
  });

  useEffect(() => {
    fetchProveedores();
  }, [filterCategoria]);

  const fetchProveedores = async () => {
    try {
      setLoading(true);
      const params = filterCategoria !== "all" ? `?categoria=${filterCategoria}` : "";
      const res = await axios.get(`${API}/proveedores-gasto${params}`, { withCredentials: true });
      setProveedores(res.data);
    } catch (error) {
      toast.error("Error al cargar proveedores");
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    if (!confirm("¿Cargar los proveedores predefinidos? Esto solo funciona si la base está vacía.")) return;
    try {
      setSeeding(true);
      const res = await axios.post(`${API}/proveedores-gasto/seed`, {}, { withCredentials: true });
      if (res.data.seeded) {
        toast.success(res.data.message);
        fetchProveedores();
      } else {
        toast.info(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cargar datos");
    } finally {
      setSeeding(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/proveedores-gasto/${editingItem.id}`, formData, { withCredentials: true });
        toast.success("Proveedor actualizado");
      } else {
        await axios.post(`${API}/proveedores-gasto`, formData, { withCredentials: true });
        toast.success("Proveedor creado");
      }
      setShowDialog(false);
      setEditingItem(null);
      resetForm();
      fetchProveedores();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      razon_social: item.razon_social || "",
      nombre_comercial: item.nombre_comercial || "",
      rfc: item.rfc || "",
      categoria: item.categoria || "otro",
      direccion: item.direccion || "",
      telefono: item.telefono || "",
      email: item.email || "",
      sitio_web: item.sitio_web || "",
      contacto: item.contacto || "",
      notas: item.notas || "",
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar este proveedor?")) return;
    try {
      await axios.delete(`${API}/proveedores-gasto/${id}`, { withCredentials: true });
      toast.success("Proveedor eliminado");
      fetchProveedores();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const resetForm = () => {
    setFormData({
      razon_social: "",
      nombre_comercial: "",
      rfc: "",
      categoria: "otro",
      direccion: "",
      telefono: "",
      email: "",
      sitio_web: "",
      contacto: "",
      notas: "",
    });
  };

  const openNewDialog = () => {
    setEditingItem(null);
    resetForm();
    setShowDialog(true);
  };

  const filteredProveedores = proveedores.filter(p => {
    const matchesSearch = 
      p.nombre_comercial?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.razon_social?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.rfc?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getCategoriaInfo = (cat) => CATEGORIAS.find(c => c.value === cat) || CATEGORIAS[4];

  // Stats by category
  const statsByCategoria = CATEGORIAS.map(c => ({
    ...c,
    count: proveedores.filter(p => p.categoria === c.value).length
  }));

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="settings-proveedores-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/configuracion/opciones")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Proveedores de Gasto</h1>
              <p className="text-slate-500">Supermercados, Gasolineras, Agencias de Autos, etc.</p>
            </div>
          </div>
          <div className="flex gap-2">
            {proveedores.length === 0 && (
              <Button onClick={handleSeed} disabled={seeding} variant="outline">
                <Download className="w-4 h-4 mr-2" />
                {seeding ? "Cargando..." : "Cargar Datos Iniciales"}
              </Button>
            )}
            <Button onClick={openNewDialog} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Proveedor
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{proveedores.length}</div>
              <div className="text-xs text-slate-500">Total</div>
            </CardContent>
          </Card>
          {statsByCategoria.map(stat => (
            <Card key={stat.value}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <stat.icon className={`w-4 h-4 ${stat.color.replace("bg-", "text-")}`} />
                  <div>
                    <div className="text-xl font-bold">{stat.count}</div>
                    <div className="text-xs text-slate-500">{stat.label}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre, razón social o RFC..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={filterCategoria} onValueChange={setFilterCategoria}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filtrar por categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {CATEGORIAS.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>NOMBRE COMERCIAL</TableHead>
                  <TableHead>RAZÓN SOCIAL</TableHead>
                  <TableHead>RFC</TableHead>
                  <TableHead>CATEGORÍA</TableHead>
                  <TableHead>SITIO WEB</TableHead>
                  <TableHead className="text-right">ACCIONES</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10">Cargando...</TableCell>
                  </TableRow>
                ) : filteredProveedores.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10 text-slate-500">
                      {proveedores.length === 0 ? "No hay proveedores. Usa 'Cargar Datos Iniciales' para comenzar." : "No hay resultados"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProveedores.map((item) => {
                    const catInfo = getCategoriaInfo(item.categoria);
                    return (
                      <TableRow key={item.id}>
                        <TableCell className="font-semibold">{item.nombre_comercial}</TableCell>
                        <TableCell className="text-sm text-slate-600">{item.razon_social}</TableCell>
                        <TableCell className="font-mono text-sm">{item.rfc}</TableCell>
                        <TableCell>
                          <Badge className={`${catInfo.color} text-white`}>
                            {catInfo.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {item.sitio_web && (
                            <a href={`https://${item.sitio_web.replace(/^https?:\/\//, '')}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                              {item.sitio_web}
                            </a>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" onClick={() => handleEdit(item)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="text-red-600" onClick={() => handleDelete(item.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingItem ? "Editar Proveedor" : "Nuevo Proveedor"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre Comercial *</Label>
                  <Input
                    required
                    value={formData.nombre_comercial}
                    onChange={(e) => setFormData({ ...formData, nombre_comercial: e.target.value })}
                    placeholder="Ej: Walmart"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <Select value={formData.categoria} onValueChange={(v) => setFormData({ ...formData, categoria: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIAS.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Razón Social *</Label>
                <Input
                  required
                  value={formData.razon_social}
                  onChange={(e) => setFormData({ ...formData, razon_social: e.target.value.toUpperCase() })}
                  placeholder="Ej: NUEVA WAL-MART DE MEXICO S. DE R.L. DE C.V."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>RFC</Label>
                  <Input
                    value={formData.rfc}
                    onChange={(e) => setFormData({ ...formData, rfc: e.target.value.toUpperCase() })}
                    placeholder="Ej: NWM9709244W4"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sitio Web</Label>
                  <Input
                    value={formData.sitio_web}
                    onChange={(e) => setFormData({ ...formData, sitio_web: e.target.value })}
                    placeholder="www.ejemplo.com"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Dirección</Label>
                <Input
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contacto</Label>
                  <Input
                    value={formData.contacto}
                    onChange={(e) => setFormData({ ...formData, contacto: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notas</Label>
                  <Input
                    value={formData.notas}
                    onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {editingItem ? "Guardar Cambios" : "Crear Proveedor"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SettingsProveedores;
